import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft, Search, MapPin, Star, ChevronRight, 
  Filter, User, Mail, Phone, MessageSquare, CheckCircle,
  Briefcase, Award, Globe, Clock, Heart, Stethoscope,
  GraduationCap, Languages, DollarSign, Building2
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, API } from "@/App";

const FindDietitian = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [referenceData, setReferenceData] = useState({
    countries: [], cities: {}, cuisines: [], languages: [], pricing_types: []
  });
  
  const [filters, setFilters] = useState({
    city: "", country: "", cuisine: "", language: "", pricing_type: ""
  });
  const [showFilters, setShowFilters] = useState(false);
  
  const [selectedListing, setSelectedListing] = useState(null);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [inquirySent, setInquirySent] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    name: user?.name || "", email: user?.email || "",
    phone: "", message: "", diabetes_type: ""
  });

  useEffect(() => {
    loadReferenceData();
    searchListings();
  }, []);

  const loadReferenceData = async () => {
    try {
      const response = await fetch(`${API}/directory/reference-data`);
      if (response.ok) setReferenceData(await response.json());
    } catch (error) {
      console.error("Failed to load reference data:", error);
    }
  };

  const searchListings = async () => {
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (filters.city) params.append("city", filters.city);
      if (filters.country) params.append("country", filters.country);
      if (filters.cuisine) params.append("cuisine", filters.cuisine);
      if (filters.language) params.append("language", filters.language);
      if (filters.pricing_type) params.append("pricing_type", filters.pricing_type);
      
      const response = await fetch(`${API}/directory/search?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setListings(data.listings || []);
      }
    } catch (error) {
      toast.error("Failed to search providers");
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const clearFilters = () => {
    setFilters({ city: "", country: "", cuisine: "", language: "", pricing_type: "" });
  };

  const hasActiveFilters = Object.values(filters).some(v => v);

  const handleInquire = (listing) => {
    setSelectedListing(listing);
    setInquirySent(false);
    setInquiryForm({
      name: user?.name || "", email: user?.email || "",
      phone: "", message: "", diabetes_type: ""
    });
    setShowInquiryModal(true);
  };

  const submitInquiry = async (e) => {
    e.preventDefault();
    setInquiryLoading(true);
    try {
      const response = await fetch(`${API}/directory/inquire`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ listing_id: selectedListing.listing_id, ...inquiryForm })
      });
      const data = await response.json();
      if (response.ok) {
        setInquirySent(true);
        toast.success("Inquiry sent successfully!");
      } else {
        toast.error(data.detail || "Failed to send inquiry");
      }
    } catch (error) {
      toast.error("Failed to send inquiry");
    } finally {
      setInquiryLoading(false);
    }
  };

  const getPricingLabel = (type) => {
    const labels = {
      free_consultation: "Free Consultation",
      per_consultation: "Per Consultation",
      monthly_subscription: "Monthly Subscription",
      insurance_only: "Insurance/HMO Only",
      paid: "Paid Services",
      insurance: "Insurance Accepted"
    };
    return labels[type] || type;
  };

  const getCuisineLabel = (cuisine) => {
    const labels = {
      african: "African", nigerian: "Nigerian", western: "Western",
      asian: "Asian", middle_eastern: "Middle Eastern", vegetarian: "Vegetarian",
      mediterranean: "Mediterranean", low_carb: "Low Carb/Keto", vegan: "Vegan"
    };
    return labels[cuisine] || cuisine.charAt(0).toUpperCase() + cuisine.slice(1);
  };

  const formatFee = (listing) => {
    if (listing.consultation_fee) {
      return `\u20A6${Number(listing.consultation_fee).toLocaleString()}`;
    }
    if (listing.pricing_type === "free_consultation") return "Free";
    return null;
  };

  const getProfessionalLabel = (type) => {
    const labels = {
      doctor: "Doctor", nurse: "Nurse", pharmacist: "Pharmacist",
      dietitian: "Dietitian/Nutritionist", diabetes_educator: "Diabetes Educator"
    };
    return labels[type] || type || "Physician";
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-orange-100 bg-background/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-muted-foreground hover:text-secondary transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-xl font-bold text-secondary">Find a Healthcare Provider</h1>
          </div>
          <Button
            variant="outline" size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={`rounded-full ${hasActiveFilters ? "border-primary text-primary" : ""}`}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-1" />
            Filters
            {hasActiveFilters && (
              <Badge className="ml-1 bg-primary text-white text-xs h-5 w-5 rounded-full p-0 flex items-center justify-center">
                {Object.values(filters).filter(v => v).length}
              </Badge>
            )}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Search Filters Panel */}
        {showFilters && (
          <Card className="bg-white rounded-3xl border border-orange-100/50">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-secondary">Search Filters</h3>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">Clear all</Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Select value={filters.country} onValueChange={(v) => setFilters({ ...filters, country: v, city: "" })}>
                    <SelectTrigger className="rounded-xl" data-testid="filter-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                    <SelectContent>{referenceData.countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Select value={filters.city} onValueChange={(v) => setFilters({ ...filters, city: v })} disabled={!filters.country}>
                    <SelectTrigger className="rounded-xl" data-testid="filter-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                    <SelectContent>{(referenceData.cities[filters.country] || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Language</Label>
                  <Select value={filters.language} onValueChange={(v) => setFilters({ ...filters, language: v })}>
                    <SelectTrigger className="rounded-xl" data-testid="filter-language"><SelectValue placeholder="Any language" /></SelectTrigger>
                    <SelectContent>{referenceData.languages.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Pricing</Label>
                  <Select value={filters.pricing_type} onValueChange={(v) => setFilters({ ...filters, pricing_type: v })}>
                    <SelectTrigger className="rounded-xl" data-testid="filter-pricing"><SelectValue placeholder="Any pricing" /></SelectTrigger>
                    <SelectContent>{referenceData.pricing_types.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={searchListings} disabled={searching} className="w-full rounded-full bg-primary text-white hover:bg-primary/90" data-testid="apply-filters-btn">
                {searching ? "Searching..." : "Apply Filters"}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse flex flex-col items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/20" />
              <p className="text-muted-foreground">Finding healthcare providers...</p>
            </div>
          </div>
        ) : listings.length === 0 ? (
          <Card className="bg-white rounded-3xl border border-orange-100/50 p-8 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                <Search className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary">No Providers Found</h3>
              <p className="text-muted-foreground max-w-md">No healthcare providers match your criteria. Try adjusting your filters.</p>
              {hasActiveFilters && <Button variant="outline" onClick={clearFilters} className="rounded-full">Clear Filters</Button>}
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{listings.length} provider{listings.length !== 1 ? "s" : ""} found</p>
            
            {listings.map((listing) => (
              <Card
                key={listing.listing_id}
                className="bg-white rounded-3xl border border-orange-100/50 hover:shadow-soft-lift transition-all cursor-pointer overflow-hidden"
                onClick={() => setSelectedListing(listing)}
                data-testid={`listing-card-${listing.listing_id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-16 h-16 rounded-2xl overflow-hidden bg-primary/10 flex-shrink-0">
                      {listing.photo_url ? (
                        <img src={listing.photo_url} alt={listing.display_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-8 h-8 text-primary/60" />
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading font-bold text-secondary text-base truncate" data-testid="provider-name">
                          {listing.display_name}
                        </h3>
                        {listing.is_featured && (
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" data-testid="featured-star" />
                        )}
                        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 ml-auto" />
                      </div>

                      <p className="text-sm text-muted-foreground" data-testid="provider-profession">
                        {listing.title || getProfessionalLabel(listing.professional_type)}
                      </p>

                      {(listing.organization_name || listing.partner_name) && (
                        <p className="text-sm font-semibold text-secondary" data-testid="provider-company">
                          {listing.organization_name || listing.partner_name}
                        </p>
                      )}

                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        {(listing.location_city || listing.location_country) && (
                          <span className="inline-flex items-center" data-testid="provider-location">
                            <MapPin className="w-3 h-3 mr-1" />
                            {listing.location_city}{listing.location_city && listing.location_country ? ", " : ""}{listing.location_country}
                          </span>
                        )}
                        {listing.years_experience && (
                          <span className="inline-flex items-center" data-testid="provider-experience">
                            <Briefcase className="w-3 h-3 mr-1" />
                            {listing.years_experience} yrs exp.
                          </span>
                        )}
                      </div>

                      {/* Medical Specializations */}
                      {listing.specializations?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1" data-testid="provider-specializations">
                          {listing.specializations.slice(0, 3).map((s, i) => (
                            <Badge key={i} variant="outline" className="text-xs rounded-full text-primary border-primary/30 px-2 py-0">
                              {s}
                            </Badge>
                          ))}
                          {listing.specializations.length > 3 && (
                            <Badge variant="outline" className="text-xs rounded-full text-muted-foreground px-2 py-0">
                              +{listing.specializations.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}

                      {/* Fee */}
                      {formatFee(listing) && (
                        <p className="mt-2 text-sm font-bold text-primary" data-testid="provider-fee">
                          {formatFee(listing)}
                          {listing.pricing_type && listing.pricing_type !== "free_consultation" && (
                            <span className="font-normal text-muted-foreground text-xs ml-1">/ consultation</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Listing Detail Modal */}
      <Dialog open={!!selectedListing && !showInquiryModal} onOpenChange={() => setSelectedListing(null)}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto p-0">
          {selectedListing && (
            <>
              {/* Header with photo */}
              <div className="p-6 pb-4">
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 rounded-2xl overflow-hidden bg-primary/10 flex-shrink-0">
                    {selectedListing.photo_url ? (
                      <img src={selectedListing.photo_url} alt={selectedListing.display_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-10 h-10 text-primary/60" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <DialogTitle className="font-heading text-xl">{selectedListing.display_name}</DialogTitle>
                      {selectedListing.is_featured && (
                        <Star className="w-5 h-5 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                      )}
                    </div>
                    <DialogDescription className="mt-0.5">
                      {selectedListing.title || getProfessionalLabel(selectedListing.professional_type)}
                    </DialogDescription>
                    {selectedListing.credentials && (
                      <Badge variant="outline" className="mt-1 text-xs text-primary border-primary/30">{selectedListing.credentials}</Badge>
                    )}
                    {(selectedListing.organization_name || selectedListing.partner_name) && (
                      <p className="text-sm font-semibold text-secondary mt-1" data-testid="detail-company">
                        <Building2 className="w-3.5 h-3.5 inline mr-1" />
                        {selectedListing.organization_name || selectedListing.partner_name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="px-6 pb-6 space-y-4">
                {/* Bio */}
                {selectedListing.bio && (
                  <>
                    <p className="text-sm text-muted-foreground leading-relaxed">{selectedListing.bio}</p>
                    <Separator />
                  </>
                )}
                
                {/* Key Details Grid */}
                <div className="grid gap-3">
                  {(selectedListing.location_city || selectedListing.location_country) && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>{selectedListing.location_city}{selectedListing.location_city && selectedListing.location_country ? ", " : ""}{selectedListing.location_country}</span>
                    </div>
                  )}
                  {selectedListing.years_experience && (
                    <div className="flex items-center gap-2 text-sm">
                      <Briefcase className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>{selectedListing.years_experience} years of experience</span>
                    </div>
                  )}
                  {selectedListing.languages?.length > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <Globe className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>{selectedListing.languages.join(", ")}</span>
                    </div>
                  )}
                  {selectedListing.cuisine_specializations?.length > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <Heart className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>Cuisine: {selectedListing.cuisine_specializations.map(getCuisineLabel).join(", ")}</span>
                    </div>
                  )}
                  {selectedListing.accepts_insurance && (
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span>Accepts Insurance/HMO</span>
                    </div>
                  )}
                </div>

                {/* Medical Specializations */}
                {selectedListing.specializations?.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="font-medium text-secondary mb-2 flex items-center gap-2 text-sm">
                        <Stethoscope className="w-4 h-4 text-primary" />
                        Medical Specializations
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedListing.specializations.map((s, i) => (
                          <Badge key={i} variant="outline" className="rounded-full text-primary border-primary/30">{s}</Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Qualifications */}
                {selectedListing.qualifications?.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <h4 className="font-medium text-secondary mb-2 flex items-center gap-2 text-sm">
                        <GraduationCap className="w-4 h-4 text-primary" />
                        Qualifications
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedListing.qualifications.map((q, i) => (
                          <Badge key={i} variant="outline" className="rounded-full">{q}</Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                
                {/* Pricing Section */}
                <Separator />
                <div className="bg-green-50 p-4 rounded-2xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-700 font-medium">{getPricingLabel(selectedListing.pricing_type)}</p>
                      {selectedListing.pricing_details && (
                        <p className="text-xs text-green-600 mt-0.5">{selectedListing.pricing_details}</p>
                      )}
                    </div>
                    {selectedListing.consultation_fee && (
                      <p className="text-xl font-bold text-green-700" data-testid="detail-fee">
                        {"\u20A6"}{Number(selectedListing.consultation_fee).toLocaleString()}
                      </p>
                    )}
                    {selectedListing.pricing_type === "free_consultation" && !selectedListing.consultation_fee && (
                      <p className="text-xl font-bold text-green-700">Free</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="px-6 pb-6">
                <Button
                  onClick={() => handleInquire(selectedListing)}
                  className="w-full rounded-full bg-primary text-white hover:bg-primary/90 py-6"
                  data-testid="contact-provider-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Contact This Provider
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Inquiry Modal */}
      <Dialog open={showInquiryModal} onOpenChange={setShowInquiryModal}>
        <DialogContent className="rounded-3xl max-w-lg">
          {inquirySent ? (
            <>
              <DialogHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <DialogTitle className="font-heading text-xl">Inquiry Sent!</DialogTitle>
                <DialogDescription>
                  Your message has been sent to {selectedListing?.display_name}. They will contact you soon.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button onClick={() => { setShowInquiryModal(false); setSelectedListing(null); }} className="w-full rounded-full bg-primary text-white hover:bg-primary/90">Done</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Contact {selectedListing?.display_name}</DialogTitle>
                <DialogDescription>Fill out the form and the provider will get back to you.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submitInquiry} className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="inquiry-name">Your Name</Label>
                    <Input id="inquiry-name" value={inquiryForm.name} onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })} className="rounded-xl" required data-testid="inquiry-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inquiry-email">Email</Label>
                    <Input id="inquiry-email" type="email" value={inquiryForm.email} onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })} className="rounded-xl" required data-testid="inquiry-email-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="inquiry-phone">Phone (Optional)</Label>
                    <Input id="inquiry-phone" type="tel" value={inquiryForm.phone} onChange={(e) => setInquiryForm({ ...inquiryForm, phone: e.target.value })} className="rounded-xl" data-testid="inquiry-phone-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inquiry-diabetes">Diabetes Type</Label>
                    <Select value={inquiryForm.diabetes_type} onValueChange={(v) => setInquiryForm({ ...inquiryForm, diabetes_type: v })}>
                      <SelectTrigger className="rounded-xl" data-testid="inquiry-diabetes-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="type1">Type 1</SelectItem>
                        <SelectItem value="type2">Type 2</SelectItem>
                        <SelectItem value="prediabetes">Prediabetes</SelectItem>
                        <SelectItem value="monitoring">Just monitoring</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inquiry-message">Message (Optional)</Label>
                  <Textarea id="inquiry-message" value={inquiryForm.message} onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })} className="rounded-xl min-h-[100px]" placeholder="Tell the provider about your goals or concerns..." data-testid="inquiry-message-input" />
                </div>
                <DialogFooter className="gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowInquiryModal(false)} className="rounded-full">Cancel</Button>
                  <Button type="submit" disabled={inquiryLoading} className="rounded-full bg-primary text-white hover:bg-primary/90" data-testid="submit-inquiry-btn">
                    {inquiryLoading ? "Sending..." : "Send Inquiry"}
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FindDietitian;
